import { combineLatest, merge, Observable, of, OperatorFunction, pairwise, Subject } from 'rxjs';
import { debounceTime, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Actions, ActionType, ofActionDispatched, Store } from '@ngxs/store';

import { getGlobalFilters } from '@app/core/store/states/api/api-state.utilities';
import { GlobalFiltersState } from '@app/core/store/states/global-filters/global-filters.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { mergeApiRequests } from '@app/core/utilities/api.utilities';
import { assertArray } from '@app/core/utilities/array.utilities';
import { isDeepEqual } from '@app/core/utilities/object.utilities';
import { getFilterAndSearchRefinersFromCurrentState, RefinerKeys } from '@app/core/utilities/refiners.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { ApiInstanceService } from '../../api/api-instance.service';
import { ApiListMutator } from '../../api/api-list-mutator.class';
import {
  ApiGetAction,
  ApiListConnect,
  ApiListDisconnect,
  ApiListEmitAction,
  ApiListLoadAll,
  ApiListLoadMore,
  ApiListRefresh,
  ApiListReset,
  ApiListSearch,
  ApiListSetFilterValues,
  ApiListSetLimit,
  ApiListSetPage,
  ApiListSetSearchValues,
  ApiListSort,
  ApiListUpdateFilterValues,
} from '../models/api-action.models';
import { ApiListStoreOptions, ApiStoreFilterValues } from '../models/api-store.models';
import { ApiSetMetadata } from '../states/api/api-state.actions';

@Injectable()
export class ApiStoreConnectService {
  private connecting$ = new Subject<ActionType>();

  constructor(
    private actions: Actions,
    private ai: ApiInstanceService,
    private store: Store,
  ) {}

  connectList<T extends object = object>(actionType: ActionType, options: ApiListStoreOptions<T>): void {
    this.connecting$.next(actionType);

    let mutator: ApiListMutator<T>;

    const disconnect = merge(
      this.ofAction(ApiListConnect, actionType),
      this.ofAction(ApiListDisconnect, actionType),
      this.connecting$.pipe(filter((type) => type === actionType)),
    );

    const globalFilters$: Observable<ApiStoreFilterValues<T>> = options.storeSettings?.useGlobalSelectors
      ? combineLatest([
          this.store.select(GlobalFiltersState.filtersValues),
          this.store.select(RouterNavState.queryParams),
        ]).pipe(
          map(([filterValues, params]) => {
            const filters = getFilterAndSearchRefinersFromCurrentState(
              params,
              {},
              { ...filterValues },
              {
                includeAdGroup: true,
                includeCriteria: true,
                forApiMutator: true,
                refinerKeys: [RefinerKeys.EMPTY, RefinerKeys.TARGET],
              },
            );
            if (options?.excludeRefiners?.filterValues) {
              options.excludeRefiners.filterValues.map((key) => {
                if (filters.filterValues[key] != undefined) delete filters.filterValues[key];
              });
            }

            return filters;
          }),
          map((refiners) => getGlobalFilters(refiners, options.storeSettings)),
          takeUntil(disconnect),
        )
      : of({});

    options.init = {
      ...options.init,
      ...getGlobalFilters(lastValue(globalFilters$, {}), options.storeSettings, options.init),
    };

    const connect = () =>
      this.ofAction(actionType)
        .pipe(
          tap((action: ApiGetAction<T>) => (mutator = this.createListMutator(action, options))),
          switchMap(() => this.ofListMutateAction(actionType)),
          takeUntil(disconnect),
        )
        .subscribe((action) => this.onListMutateAction<T>(mutator, action));

    if (options.storeSettings?.useGlobalSelectors) {
      globalFilters$
        .pipe(debounceTime(options.storeSettings?.globalSelectorDebounce || 0), pairwise(), takeUntil(disconnect))
        .subscribe(([prevFilters, newFilters]) => {
          if (!isDeepEqual(prevFilters.filterValues, newFilters.filterValues)) {
            this.store.dispatch(new ApiListSetFilterValues(actionType, newFilters.filterValues));
          }

          if (!isDeepEqual(prevFilters.searchValues, newFilters.searchValues)) {
            this.store.dispatch(new ApiListSetSearchValues(actionType, newFilters.searchValues));
          }
        });
    }

    const operators: [OperatorFunction<any, any>?] = options.storeSettings?.waitForConnect ? [] : [startWith(0)];

    this.ofAction(ApiListConnect, actionType)
      .pipe(...operators)
      .subscribe(() => connect());
  }

  private onListMutateAction<T extends object>(mutator: ApiListMutator<T>, action: unknown): void {
    if (!mutator) {
      return;
    }

    if (action instanceof ApiListSearch) {
      mutator.searchTerm$.next(action.searchTerm);
    } else if (action instanceof ApiListUpdateFilterValues) {
      mutator.filterValues$.next({
        ...mutator.filterValues$.getValue(),
        ...action.filterValues,
      });
    } else if (action instanceof ApiListSetFilterValues) {
      mutator.filterValues$.next(action.filterValues);
    } else if (action instanceof ApiListSetSearchValues) {
      mutator.searchValues$.next(action.searchValues);
    } else if (action instanceof ApiListLoadMore) {
      mutator.loadMore();
    } else if (action instanceof ApiListSetPage) {
      mutator.setPage(action.pageIndex);
    } else if (action instanceof ApiListSetLimit) {
      mutator.setLimit(action.limit);
    } else if (action instanceof ApiListLoadAll) {
      mutator.loadAll();
    } else if (action instanceof ApiListSort) {
      mutator.sortValues$.next(action.sortValues);
    } else if (action instanceof ApiListReset) {
      mutator.reset();
    } else if (action instanceof ApiListRefresh) {
      mutator.refresh();
    }
  }

  private ofListMutateAction(actionType: ActionType): Observable<unknown> {
    return this.ofAction(
      [
        ApiListSearch,
        ApiListSort,
        ApiListSetPage,
        ApiListSetLimit,
        ApiListUpdateFilterValues,
        ApiListSetFilterValues,
        ApiListSetSearchValues,
        ApiListLoadMore,
        ApiListLoadAll,
        ApiListReset,
        ApiListRefresh,
      ],
      actionType,
    );
  }

  private ofAction(dispatched: ActionType | ActionType[], actionType?: unknown): Observable<unknown> {
    return this.actions.pipe(
      ofActionDispatched(...assertArray(dispatched)),
      filter((action: { action: ActionType }) => !actionType || action.action === actionType),
    );
  }

  private createListMutator<T extends object>(
    action: ApiGetAction<T>,
    options: ApiListStoreOptions<T>,
  ): ApiListMutator<T> {
    const mutator = new ApiListMutator(
      (req) => this.ai.getList(req),
      mergeApiRequests(options?.init, action.options, true),
      options?.settings,
    );

    mutator
      .pipe(takeUntil(this.ofAction(ApiListDisconnect, action)))
      .subscribe((response) => this.store.dispatch(new (ApiListEmitAction(action))(response)));

    combineLatest([
      combineLatest([
        mutator.loading$,
        mutator.refreshing$,
        mutator.firstLoad$,
        mutator.loadingMore$,
        mutator.searchTerm$,
        mutator.sortValues$,
        mutator.filterValues$,
        mutator.searchValues$,
      ]),
      combineLatest([
        mutator.canLoadMore$.pipe(startWith(undefined)),
        mutator.pagination$.pipe(startWith(undefined)),
        mutator.total$.pipe(startWith(undefined)),
        mutator.noData$.pipe(startWith(undefined)),
      ]),
    ])
      .pipe(takeUntil(this.ofAction(ApiListDisconnect, action)))
      .subscribe(
        ([
          [loading, refreshing, firstLoad, loadingMore, search, sortValues, filterValues, searchValues],
          [canLoadMore, pagination, total, noData],
        ]) => {
          this.store.dispatch(
            new ApiSetMetadata(action, {
              loading,
              refreshing,
              firstLoad,
              pagination,
              total,
              noData,
              loadingMore,
              search,
              canLoadMore,
              sortValues,
              filterValues,
              searchValues,
              trackById: mutator.trackById,
            }),
          );
        },
      );

    return mutator;
  }
}
